/**
 * Cross-file consistency gate (Section 9.5).
 *
 * "assert the guide item's identifier used in items/guide.json, the main.js
 * itemUse guard, and recipes/guide.json result are byte-identical." This is the
 * single most common silent breakage and is a HARD gate on export.
 *
 * We deliberately re-parse the SERIALIZED output rather than trust the model,
 * so a bug in any builder is caught before a broken pack is produced.
 */

import { guideFullId } from "../model/identifiers";
import type { Project } from "../model/types";
import type { ExportFile } from "./files";

export class ConsistencyError extends Error {
  constructor(
    message: string,
    public readonly details: string[],
  ) {
    super(message);
    this.name = "ConsistencyError";
  }
}

function contentString(file: ExportFile | undefined): string | undefined {
  if (!file) return undefined;
  return typeof file.content === "string" ? file.content : undefined;
}

/** Extract the guide item's identifier from the serialized item JSON. */
function guideIdFromItem(project: Project, files: ExportFile[]): string | undefined {
  const suffix = `/items/${project.guideItemId}.json`;
  const file = files.find((f) => f.path.endsWith(suffix));
  const str = contentString(file);
  if (!str) return undefined;
  try {
    return JSON.parse(str)?.["minecraft:item"]?.description?.identifier;
  } catch {
    return undefined;
  }
}

/** Extract the itemUse guard target from the serialized main.js. */
function guideIdFromScript(files: ExportFile[]): string | undefined {
  const file = files.find((f) => f.path.endsWith("/scripts/main.js"));
  const str = contentString(file);
  if (!str) return undefined;
  const match = str.match(/const GUIDE_ITEM_ID = "([^"]*)"/);
  return match?.[1];
}

/**
 * Extract the result item of the recipe that produces the guide item. We look
 * at every recipe's serialized result rather than assume a filename.
 */
function guideIdFromRecipe(project: Project, files: ExportFile[]): string | undefined {
  const expected = guideFullId(project);
  const recipeFiles = files.filter((f) => f.path.includes("/recipes/") && f.path.endsWith(".json"));
  for (const file of recipeFiles) {
    const str = contentString(file);
    if (!str) continue;
    try {
      const parsed = JSON.parse(str);
      const body = parsed["minecraft:recipe_shapeless"] ?? parsed["minecraft:recipe_shaped"];
      const resultItem: string | undefined = body?.result?.item;
      if (resultItem === expected) return resultItem;
    } catch {
      /* ignore malformed */
    }
  }
  return undefined;
}

/**
 * Throws ConsistencyError if the three guide-id sites are not byte-identical.
 * Returns the agreed id on success.
 */
export function assertGuideConsistency(project: Project, files: ExportFile[]): string {
  const expected = guideFullId(project);
  const fromItem = guideIdFromItem(project, files);
  const fromScript = guideIdFromScript(files);
  const fromRecipe = guideIdFromRecipe(project, files);

  const details = [
    `expected (from model): ${expected}`,
    `items/${project.guideItemId}.json → description.identifier: ${fromItem ?? "(missing)"}`,
    `scripts/main.js → itemUse guard: ${fromScript ?? "(missing)"}`,
    `recipes/*.json → result producing guide: ${fromRecipe ?? "(no recipe produces the guide item)"}`,
  ];

  if (fromItem === undefined) {
    throw new ConsistencyError("Guide item definition is missing or unparseable.", details);
  }
  if (fromScript === undefined) {
    throw new ConsistencyError("main.js itemUse guard could not be located.", details);
  }
  // A recipe producing the guide item is recommended but not strictly required.
  const mustMatch = [fromItem, fromScript];
  if (fromRecipe !== undefined) mustMatch.push(fromRecipe);

  const allEqual = mustMatch.every((v) => v === mustMatch[0]) && mustMatch[0] === expected;
  if (!allEqual) {
    throw new ConsistencyError(
      "Guide item identifier is not byte-identical across items, script, and recipe.",
      details,
    );
  }
  return expected;
}

/** Non-throwing variant for the editor status panel. */
export function checkGuideConsistency(
  project: Project,
  files: ExportFile[],
): { ok: boolean; message: string; details: string[] } {
  try {
    const id = assertGuideConsistency(project, files);
    return { ok: true, message: `Guide id "${id}" is consistent across all files.`, details: [] };
  } catch (err) {
    if (err instanceof ConsistencyError) return { ok: false, message: err.message, details: err.details };
    return { ok: false, message: String(err), details: [] };
  }
}
